import React from "react";
import { DollarSign } from "lucide-react";

// Flat toll presets (AUD). Keep in sync with the copies previously hardcoded
// in CreateJob.jsx / JobStatus.jsx.
export const TOLL_PRESETS = [
  { key: "kuraby",    label: "Logan Motorway (Kuraby North)",  amount: 11.75 },
  { key: "heathwood", label: "Logan Motorway (Heathwood East)", amount: 12.43 },
  { key: "gateway",   label: "Gateway Motorway",                amount: 19.91 },
];

export function tollTotal(selected, otherAmount) {
  const presetTotal = TOLL_PRESETS.filter((o) => (selected || []).includes(o.key)).reduce((s, o) => s + o.amount, 0);
  return +(presetTotal + (Number(otherAmount) || 0)).toFixed(2);
}

// Backend stores a single flat `toll` dollar amount (see backend/server.py) —
// it has no record of which presets were combined to produce it. Best-effort
// reverse-map an existing job's stored total back into checkbox state by
// trying every combination of the (few) known presets for an exact match;
// anything that doesn't match a combination falls back to "Other".
export function detectTollSelection(toll) {
  const amount = Number(toll) || 0;
  if (amount <= 0) return { selected: [], otherAmount: "" };
  const n = TOLL_PRESETS.length;
  for (let mask = (1 << n) - 1; mask >= 1; mask--) {
    const combo = TOLL_PRESETS.filter((_, i) => mask & (1 << i));
    const sum = +combo.reduce((s, o) => s + o.amount, 0).toFixed(2);
    if (Math.abs(sum - amount) < 0.005) return { selected: combo.map((o) => o.key), otherAmount: "" };
  }
  return { selected: [], otherAmount: String(amount) };
}

// Multiple simultaneous toll selections (e.g. Kuraby AND Gateway on the same
// job) instead of a single dropdown that only holds one preset — their
// amounts sum into one flat toll total.
export default function TollMultiSelect({ selected, otherAmount, onChangeSelected, onChangeOtherAmount, disabled }) {
  const toggle = (key) => {
    if (disabled) return;
    onChangeSelected(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };
  const otherOn = otherAmount !== "" && otherAmount != null;
  const total = tollTotal(selected, otherAmount);

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5 space-y-1.5">
      {TOLL_PRESETS.map((o) => (
        <label key={o.key} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
          <input
            type="checkbox"
            disabled={disabled}
            checked={selected.includes(o.key)}
            onChange={() => toggle(o.key)}
            className="accent-[#19e6c8]"
          />
          {o.label} (${o.amount.toFixed(2)})
        </label>
      ))}
      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
        <input
          type="checkbox"
          disabled={disabled}
          checked={otherOn}
          onChange={() => onChangeOtherAmount(otherOn ? "" : "0")}
          className="accent-[#19e6c8]"
        />
        Other (specify)
      </label>
      {otherOn && (
        <div className="relative">
          <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
          <input
            type="number" step="0.01" min="0"
            disabled={disabled}
            value={otherAmount}
            onChange={(e) => onChangeOtherAmount(e.target.value)}
            className="df-input-rect !pl-8"
            placeholder="0.00"
            data-testid="toll-manual"
          />
        </div>
      )}
      <p className="text-[11px] text-white/40 pt-0.5">
        {total > 0 ? `Toll total: $${total.toFixed(2)}` : "No toll selected"}
      </p>
    </div>
  );
}
